import * as z from 'zod/mini'
import { EditUserBody, Gender, ProfileField, UserInfo, userFields } from '@seiri/shared/common'
import { formPhone, requiredFormDate, requiredText } from './form-schema'

/** The signed-in person's own profile, as the form holds it: the picker's `Date`, and `null` for no gender. */
export interface ProfileFormModel {
  firstName: string
  lastName: string
  phone: string
  birthDate: Date | null
  gender: Gender | null
}

/**
 * The profile form's rules (docs/account.md). Every field is optional; `required` - `requiredProfileFields` of the
 * person - makes the ones they must keep required, so the form refuses what the API would.
 */
export const profileFormModel = (required: readonly ProfileField[] = []) => {
  const text = (field: ProfileField, schema: z.ZodMiniString) =>
    required.includes(field) ? schema.check(requiredText) : schema
  return z.object({
    firstName: text('firstName', userFields.firstName),
    lastName: text('lastName', userFields.lastName),
    phone: formPhone(),
    birthDate: required.includes('birthDate') ? requiredFormDate() : z.nullable(z.date()),
    gender: z.nullable(userFields.gender),
  })
}

export const toProfileFormModel = (user: UserInfo | null): ProfileFormModel => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  phone: user?.phone || '',
  birthDate: user?.birthDate ? new Date(user.birthDate) : null,
  gender: user?.gender ?? null,
})

/** Sends every field, so emptying one clears it: `''` for a text, `null` for the birth date and the gender. */
export const toEditUserBody = (model: ProfileFormModel): EditUserBody => ({
  firstName: model.firstName,
  lastName: model.lastName,
  phone: model.phone,
  birthDate: model.birthDate ? model.birthDate.toISOString() : null,
  gender: model.gender,
})
